import type { ComponentProps, ReactElement } from "react";
import { memo, useCallback, useMemo, useRef } from "react";

import type { ExpandedRowRender } from "rc-table/lib/interface";
import { useDispatch, useSelector } from "react-redux";

import { SearchResultsTable } from "app/pages/sales-intelligence/common-components/search-results-table";
import { usePinScrollOnExpand } from "app/pages/sales-intelligence/common-components/search-results-table/hooks/usePinScrollOnExpand";
import { useTableScrollHandlers } from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useTableScrollHandlers";
import { useVirtualTableScrollY } from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useVirtualTableScrollY";
import useHighlightColumns from "app/pages/sales-intelligence/modules/leads-search/hooks/useHighlightColumns";

import { useTableUnmountKey } from "../../../hooks/useTableUnmountKey";
import { setSelectedRows, setTableRequestParams } from "../../../store/action-creators";
import {
    selectActiveLeadsType,
    selectActiveSearchResultsDto,
    selectExpandedRowKeys,
    selectIsRowSelectionEnabled,
    selectSelectedRowKeys,
} from "../../../store/selectors";
import type { BaseTableRow, LeadsSearchRowWithKey, LeadsSearchTableData } from "../../../types";

import { StyledScrollableTableContentWithHighlights } from "./styles";

const DEFAULT_TABLE_DATA = { columns: [], rows: [], totalCount: 0 };

type TableProps<R extends object> = ComponentProps<typeof SearchResultsTable<R>>;

type Props<R> = {
    isLoading: boolean;
    data?: LeadsSearchTableData<R>;
    highlightedRowSelector?: (row: R) => boolean;
    activeRowSelector?: (row: R) => boolean;
    renderExpandedRow: ExpandedRowRender<LeadsSearchRowWithKey>;
};

const SearchResultsTableContainer = <R extends LeadsSearchRowWithKey>({
    isLoading,
    data = DEFAULT_TABLE_DATA,
    highlightedRowSelector,
    activeRowSelector,
    renderExpandedRow,
}: Props<R>): ReactElement => {
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const activeTabId = useSelector(selectActiveLeadsType);
    const expandedRowKeys = useSelector(selectExpandedRowKeys);
    const isRowSelectionEnabled = useSelector(selectIsRowSelectionEnabled);
    const selectedRowKeys = useSelector(selectSelectedRowKeys);
    const searchRequestDto = useSelector(selectActiveSearchResultsDto);

    const handleSortChange = useCallback<NonNullable<TableProps<R>["onSort"]>>(
        ({ order, columnKey }): void => {
            dispatch(
                setTableRequestParams({
                    leadsType: activeTabId,
                    orderBy: columnKey as string,
                    asc: order === "ascend",
                }),
            );
        },
        [activeTabId, dispatch],
    );

    const selectionProps: TableProps<R>["selection"] = useMemo(
        () => ({
            enabled: isRowSelectionEnabled,
            selectedRowKeys,
            onChange(_keys, rows, _info) {
                dispatch(setSelectedRows(rows));
            },
        }),
        [dispatch, isRowSelectionEnabled, selectedRowKeys],
    );

    const paginationProps: TableProps<R>["pagination"] = useMemo(
        () => ({
            pageSize: searchRequestDto.pageSize,
            total: data.totalCount,
            current: searchRequestDto.page,
            onChange(_currentPage: number, _pageSize: number, nextPage) {
                dispatch(setTableRequestParams({ leadsType: activeTabId, page: nextPage }));
            },
        }),
        [searchRequestDto.pageSize, searchRequestDto.page, data.totalCount, dispatch, activeTabId],
    );

    const expandableProps = useMemo(
        (): TableProps<R>["expandable"] => ({
            showExpandColumn: false,
            expandedRowKeys,
            expandedRowRender: renderExpandedRow,
        }),
        [expandedRowKeys, renderExpandedRow],
    );

    const tableUnmountKey = useTableUnmountKey(data.columns);

    const columnConfig = useHighlightColumns(tableWrapperRef, data.columns, isLoading);

    const scrollY = useVirtualTableScrollY(tableWrapperRef, expandedRowKeys.length > 0);
    usePinScrollOnExpand(tableWrapperRef, expandedRowKeys.length > 0);
    const { onScroll } = useTableScrollHandlers(tableWrapperRef);

    return (
        <StyledScrollableTableContentWithHighlights
            key={tableUnmountKey}
            ref={tableWrapperRef}
        >
            <SearchResultsTable<R>
                isLoading={isLoading}
                data={data.rows as R[]}
                columnConfig={columnConfig}
                selection={selectionProps}
                pagination={paginationProps}
                expandable={expandableProps}
                highlightedRowSelector={highlightedRowSelector}
                activeRowSelector={activeRowSelector}
                virtual
                scroll={{ y: scrollY }}
                sticky={false}
                onSort={handleSortChange}
                onScroll={onScroll}
            />
        </StyledScrollableTableContentWithHighlights>
    );
};

export default memo(SearchResultsTableContainer) as <R extends BaseTableRow>(props: Props<R>) => ReactElement;
